/**
 * OnlineSalesPage
 * 
 * Página para gerenciar pedidos online do e-commerce
 * Formato visual similar à página de auditorias de estoque
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, Eye, X, Filter, ArrowRight,
  Package, Truck, CheckCircle2, Clock, AlertCircle, FileText, Download, Printer, RefreshCw, Receipt,
  SlidersVertical, Ban
} from 'lucide-react';
import { orderService } from '../../../../services/api';
import NfceViewerModal from '../../../../components/ERP/Vendas/NfceViewerModal/NfceViewerModal';
import './OnlineSalesPage.css';
import '../../Estoque/EntradaMercadoria/IncomingDashboard.css';

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  created_at: string;
  total_amount: number;
  status: string;
  items_count?: number;
  pagseguro_transaction_id?: string;
  payment_method?: string;
  payment_timing?: string;
  sale_id?: number | null;
  customer_phone?: string;
  has_cupom?: boolean;
  has_nfce?: boolean;
  fiscal_document_type?: string | null;
  receipt_sale_id?: number | null;
  linked_sale_id?: number | null;
  linked_sale_number?: string | null;
}

interface WorkflowStep {
  label: string;
  count: number;
  icon: any;
  color: string;
  bg: string;
}

const AWAITING_PRESALE_LABEL = 'Aguardando na pré-venda';
const PRESALE_ISSUED_LABEL = 'Pré-venda emitida';

function OnlineSalesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    { label: 'Pendente', count: 0, icon: Clock, color: 'step-amber', bg: '' },
    { label: 'Pagamento Confirmado', count: 0, icon: CheckCircle2, color: 'step-emerald', bg: '' },
    { label: AWAITING_PRESALE_LABEL, count: 0, icon: Package, color: 'step-slate', bg: '' },
    { label: PRESALE_ISSUED_LABEL, count: 0, icon: FileText, color: 'step-blue', bg: '' },
    { label: 'Coletado', count: 0, icon: Truck, color: 'step-blue', bg: '' },
    { label: 'Em Transporte', count: 0, icon: Truck, color: 'step-purple', bg: '' },
    { label: 'Recebido', count: 0, icon: CheckCircle2, color: 'step-rose', bg: '' },
    { label: 'Entregue', count: 0, icon: CheckCircle2, color: 'step-emerald', bg: '' },
  ]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalOrder, setStatusModalOrder] = useState<Order | null>(null);
  const [nextStatus, setNextStatus] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [cupomModalOpen, setCupomModalOpen] = useState(false);
  const [cupomTexto, setCupomTexto] = useState('');
  const [cupomLoading, setCupomLoading] = useState(false);
  const [cupomOrderNumber, setCupomOrderNumber] = useState('');
  const [nfceModalOpen, setNfceModalOpen] = useState(false);
  const [nfceSaleId, setNfceSaleId] = useState<number | null>(null);
  const [actionsMenu, setActionsMenu] = useState<{
    orderId: number;
    top: number;
    bottom: number;
    right: number;
  } | null>(null);
  const [nfceSaleNumber, setNfceSaleNumber] = useState<string | number | null>(null);

  useEffect(() => {
    if (!actionsMenu) return undefined;
    const close = () => setActionsMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [actionsMenu]);

  const updateWorkflowCounts = (data: Order[]) => {
    setWorkflowSteps([
      { label: 'Pendente', count: data.filter(o => o.status === 'Pending').length, icon: Clock, color: 'step-amber', bg: '' },
      { label: 'Pagamento Confirmado', count: data.filter(o => o.status === 'PaymentConfirmed').length, icon: CheckCircle2, color: 'step-emerald', bg: '' },
      { label: AWAITING_PRESALE_LABEL, count: data.filter(o => o.status === 'InSeparation').length, icon: Package, color: 'step-slate', bg: '' },
      { label: PRESALE_ISSUED_LABEL, count: data.filter(o => o.status === 'PresaleIssued' || o.status === 'NfeIssued').length, icon: FileText, color: 'step-blue', bg: '' },
      { label: 'Coletado', count: data.filter(o => o.status === 'Collected').length, icon: Truck, color: 'step-blue', bg: '' },
      { label: 'Em Transporte', count: data.filter(o => o.status === 'InTransit').length, icon: Truck, color: 'step-purple', bg: '' },
      { label: 'Recebido', count: data.filter(o => o.status === 'Received').length, icon: CheckCircle2, color: 'step-rose', bg: '' },
      { label: 'Entregue', count: data.filter(o => o.status === 'Delivered').length, icon: CheckCircle2, color: 'step-emerald', bg: '' },
    ]);
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const filters: any = {};

      if (searchTerm) {
        filters.search = searchTerm;
      }

      const response = await orderService.getOrders(filters);
      if (response.success) {
        const data = response.data || [];
        setOrders(data);
        updateWorkflowCounts(data);
      }
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; icon: any }> = {
      'Pending': { label: 'Pendente', color: 'status-pending', icon: Clock },
      'PaymentConfirmed': { label: 'Pagamento Confirmado', color: 'status-confirmed', icon: CheckCircle2 },
      'InSeparation': { label: AWAITING_PRESALE_LABEL, color: 'status-separation', icon: Package },
      'PresaleIssued': { label: PRESALE_ISSUED_LABEL, color: 'status-nfe', icon: FileText },
      'NfeIssued': { label: PRESALE_ISSUED_LABEL, color: 'status-nfe', icon: FileText },
      'Collected': { label: 'Coletado', color: 'status-collected', icon: Truck },
      'InTransit': { label: 'Em Transporte', color: 'status-transit', icon: Truck },
      'Received': { label: 'Recebido', color: 'status-received', icon: CheckCircle2 },
      'Delivered': { label: 'Entregue', color: 'status-delivered', icon: CheckCircle2 },
      'Returned': { label: 'Devolvido', color: 'status-returned', icon: AlertCircle },
      'Cancelled': { label: 'Cancelado', color: 'status-cancelled', icon: X }
    };
    return statusMap[status] || { label: status, color: 'status-default', icon: Package };
  };

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetail = async (order: Order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
    setDetailLoading(true);

    try {
      const response = await orderService.getOrderById(order.id);
      if (response.success) {
        setOrderDetail(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes do pedido:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId: number, newStatus: string) => {
    try {
      const response = await orderService.updateOrderStatus(orderId, newStatus);
      if (response.success) {
        await loadOrders();
        if (orderDetail && orderDetail.id === orderId) {
          setOrderDetail(response.data);
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status do pedido');
    }
  };

  const getNextStatus = (currentStatus: string, order?: Order): string | null => {
    const timing = String(order?.payment_timing || 'online');
    const isLocalPay = timing === 'on_pickup' || timing === 'on_delivery';

    switch (currentStatus) {
      case 'Pending':
        // Pagamento local: pode ir direto para aguardando pré-venda (sem PagBank)
        return isLocalPay ? 'InSeparation' : 'PaymentConfirmed';
      case 'PaymentConfirmed':
        return 'InSeparation';
      case 'InSeparation':
        // Trava: só a pré-venda avança para PresaleIssued
        return null;
      case 'PresaleIssued':
      case 'NfeIssued':
        return 'Collected';
      case 'Collected':
        return 'InTransit';
      case 'InTransit':
        return 'Received';
      case 'Received':
        return 'Delivered';
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    return getStatusConfig(status).label;
  };

  const handlePrintExitDocument = async (order: Order) => {
    try {
      let detail = orderDetail && orderDetail.id === order.id ? orderDetail : null;
      if (!detail) {
        const response = await orderService.getOrderById(order.id);
        if (!response.success || !response.data) {
          alert('Não foi possível carregar o pedido para impressão');
          return;
        }
        detail = response.data;
      }

      const items = detail.items || [];
      const win = window.open('', '_blank', 'width=800,height=900');
      if (!win) {
        alert('Permita pop-ups para imprimir / baixar o documento');
        return;
      }

      const paid =
        detail.payment_timing === 'online' &&
        !['Pending', 'Cancelled'].includes(detail.status);
      const ship = [
        detail.shipping_street,
        detail.shipping_number && `nº ${detail.shipping_number}`,
        detail.shipping_locality,
        detail.shipping_city && `${detail.shipping_city}/${detail.shipping_region_code || ''}`,
        detail.shipping_postal_code && `CEP ${detail.shipping_postal_code}`,
      ]
        .filter(Boolean)
        .join(', ');

      const rows = items
        .map(
          (item: any) =>
            `<tr><td>${item.product_name || item.product_name_from_db || 'Produto'}</td><td style="text-align:center">${item.quantity}</td><td style="text-align:right">${formatCurrency(Number(item.unit_price) || 0)}</td><td style="text-align:right">${formatCurrency(Number(item.total_price) || 0)}</td></tr>`
        )
        .join('');

      win.document.write(`<!DOCTYPE html><html><head><title>Documento pré-venda ${detail.order_number}</title>
        <style>
          body{font-family:system-ui,sans-serif;padding:24px;color:#0f172a}
          h1{font-size:18px;margin:0 0 8px}
          .meta{font-size:13px;color:#475569;margin-bottom:16px;white-space:pre-line}
          table{width:100%;border-collapse:collapse;font-size:13px}
          th,td{border-bottom:1px solid #e2e8f0;padding:8px 4px;text-align:left}
          th{font-size:11px;text-transform:uppercase;color:#64748b}
          .total{margin-top:16px;font-size:16px;font-weight:700;text-align:right}
          .badge{display:inline-block;padding:4px 8px;border-radius:6px;background:#ecfdf5;color:#37971c;font-size:12px;font-weight:700}
          @media print{body{padding:0}}
        </style></head><body>
        <h1>Documento de saída — Pré-venda emitida</h1>
        <div class="meta"><strong>${detail.customer_name || 'Cliente'}</strong>
${detail.customer_phone ? `Tel: ${detail.customer_phone}\n` : ''}Pedido: ${detail.order_number}
${ship ? `${ship}\n` : ''}${paid ? '<span class="badge">Já pago no site</span>' : '<span class="badge" style="background:#fff7ed;color:#9a3412">Pagamento no local</span>'}
        </div>
        <table><thead><tr><th>Item</th><th>Qtd</th><th>Unit.</th><th>Total</th></tr></thead>
        <tbody>${rows}</tbody></table>
        <div class="total">Total: ${formatCurrency(Number(detail.total_amount) || 0)}</div>
        <p style="margin-top:24px;font-size:11px;color:#94a3b8">NFC-e será emitida após o fechamento no caixa.</p>
        <script>window.onload=function(){window.print()}</script>
        </body></html>`);
      win.document.close();
    } catch (err) {
      console.error(err);
      alert('Erro ao gerar documento de saída');
    }
  };

  const getReceiptSaleId = (order: Order) =>
    order.receipt_sale_id || order.linked_sale_id || null;

  const handleViewReceipt = async (order: Order) => {
    if (order.has_nfce) {
      const saleId = getReceiptSaleId(order);
      if (saleId) {
        setNfceSaleId(saleId);
        setNfceSaleNumber(order.linked_sale_number || order.order_number);
        setNfceModalOpen(true);
        return;
      }
      // Sem sale_id na lista — resolve via API NFC-e
      try {
        const response = await orderService.getNfce(order.id);
        if (response.success && response.data?.sale_id) {
          setNfceSaleId(response.data.sale_id);
          setNfceSaleNumber(response.data.sale_number || order.order_number);
          setNfceModalOpen(true);
          return;
        }
      } catch {
        /* cai no cupom simples abaixo */
      }
    }

    if (order.has_cupom || order.has_nfce || getReceiptSaleId(order)) {
      await handleViewCupom(order);
    }
  };

  const handleDownloadReceipt = async (order: Order) => {
    if (order.has_nfce) {
      // Abre visualizador fiscal (download PDF disponível no modal)
      await handleViewReceipt(order);
      return;
    }
    try {
      const response = await orderService.getCupom(order.id);
      const texto = response.data?.texto;
      if (!response.success || !texto) {
        handleViewCupom(order);
        return;
      }
      const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cupom-${order.order_number}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      handleViewCupom(order);
    }
  };

  const handleViewCupom = async (order: Order) => {
    setCupomModalOpen(true);
    setCupomLoading(true);
    setCupomTexto('');
    setCupomOrderNumber(order.order_number);
    try {
      const response = await orderService.getCupom(order.id);
      if (response.success && response.data?.texto) {
        setCupomTexto(response.data.texto);
      } else {
        setCupomTexto(response.message || 'Não foi possível gerar o cupom.');
      }
    } catch (error: any) {
      setCupomTexto(
        error.response?.data?.message || error.message || 'Erro ao carregar cupom simples.'
      );
    } finally {
      setCupomLoading(false);
    }
  };

  const handleDownloadCupom = () => {
    if (!cupomTexto || cupomLoading) return;
    const blob = new Blob([cupomTexto], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cupom-${cupomOrderNumber || 'pedido'}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleActClick = (order: Order) => {
    const next = getNextStatus(order.status, order);
    if (!next) return;
    setStatusModalOrder(order);
    setNextStatus(next);
    setShowStatusModal(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!statusModalOrder || !nextStatus) return;
    setUpdatingStatus(true);
    try {
      await handleUpdateStatus(statusModalOrder.id, nextStatus);
      setShowStatusModal(false);
      setStatusModalOrder(null);
      setNextStatus(null);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancel = async (orderId: number, refund = true) => {
    const order = orders.find((o) => o.id === orderId) || orderDetail;
    const timing = String(order?.payment_timing || 'online');
    const paidOnline =
      timing === 'online' &&
      Boolean(order?.pagseguro_transaction_id) &&
      order?.status !== 'Pending' &&
      order?.status !== 'Cancelled';

    const confirmMsg = !refund
      ? 'Cancelar sem devolver o pagamento? O valor fica com a loja. Se houver pré-venda ainda não finalizada, ela sai de aguardando.'
      : paidOnline
        ? 'Cancelar este pedido vai estornar o pagamento no PagBank (cartão/PIX) para o cliente. Continuar?'
        : 'Tem certeza que deseja cancelar este pedido?';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const response = await orderService.cancelOrder(
        orderId,
        refund
          ? 'Cancelado pelo administrador'
          : 'Cancelado sem estorno: estoque parcial, combinado com o cliente',
        { refund }
      );
      if (response.success) {
        await loadOrders();
        if (orderDetail && orderDetail.id === orderId) {
          setOrderDetail(response.data);
        }
        if (response.message) {
          window.alert(response.message);
        }
      } else {
        alert(response.message || 'Erro ao cancelar pedido');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao cancelar pedido');
    }
  };

  const getStatusFromLabel = (label: string): string[] | null => {
    const statusMap: Record<string, string[]> = {
      'Pendente': ['Pending'],
      'Pagamento Confirmado': ['PaymentConfirmed'],
      [AWAITING_PRESALE_LABEL]: ['InSeparation'],
      [PRESALE_ISSUED_LABEL]: ['PresaleIssued', 'NfeIssued'],
      'Coletado': ['Collected'],
      'Em Transporte': ['InTransit'],
      'Recebido': ['Received'],
      'Entregue': ['Delivered'],
    };
    return statusMap[label] || null;
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter) {
      const statuses = getStatusFromLabel(statusFilter);
      if (statuses && !statuses.includes(order.status)) {
        return false;
      }
    }

    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.customer_name?.toLowerCase().includes(search) ||
      order.customer_email?.toLowerCase().includes(search)
    );
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadOrders();
  };

  const menuOrder = actionsMenu
    ? orders.find((item) => item.id === actionsMenu.orderId) || null
    : null;

  const menuActions: Array<{
    key: string;
    label: string;
    icon: typeof Eye;
    danger?: boolean;
    onClick: () => void;
  }> = [];

  if (menuOrder) {
    const timing = String(menuOrder.payment_timing || 'online');
    const paidOnline =
      timing === 'online' &&
      Boolean(menuOrder.pagseguro_transaction_id) &&
      menuOrder.status !== 'Pending' &&
      menuOrder.status !== 'Cancelled';

    menuActions.push({
      key: 'view',
      label: 'Ver detalhes',
      icon: Eye,
      onClick: () => handleViewDetail(menuOrder),
    });
    if (getNextStatus(menuOrder.status, menuOrder)) {
      menuActions.push({
        key: 'act',
        label: 'Atuar',
        icon: ArrowRight,
        onClick: () => handleActClick(menuOrder),
      });
    }
    if (menuOrder.status === 'InSeparation') {
      menuActions.push({
        key: 'presale',
        label: 'Abrir na pré-venda',
        icon: Package,
        onClick: () => navigate('/erp/vendas/pre-venda'),
      });
    }
    if (menuOrder.status === 'PresaleIssued' || menuOrder.status === 'NfeIssued') {
      menuActions.push({
        key: 'exit',
        label: 'Documento de saída',
        icon: Download,
        onClick: () => handlePrintExitDocument(menuOrder),
      });
    }
    if (menuOrder.has_nfce || menuOrder.has_cupom) {
      menuActions.push({
        key: 'receipt',
        label: menuOrder.has_nfce ? 'Ver cupom fiscal' : 'Ver cupom',
        icon: menuOrder.has_nfce ? Receipt : FileText,
        onClick: () => handleViewReceipt(menuOrder),
      });
      menuActions.push({
        key: 'download',
        label: menuOrder.has_nfce ? 'Abrir cupom fiscal' : 'Baixar cupom',
        icon: Download,
        onClick: () => handleDownloadReceipt(menuOrder),
      });
    }
    if (menuOrder.status !== 'Cancelled' && menuOrder.status !== 'Delivered') {
      if (paidOnline) {
        menuActions.push({
          key: 'keep',
          label: 'Cancelar sem estorno',
          icon: Ban,
          danger: true,
          onClick: () => handleCancel(menuOrder.id, false),
        });
        menuActions.push({
          key: 'refund',
          label: 'Cancelar e estornar',
          icon: X,
          danger: true,
          onClick: () => handleCancel(menuOrder.id, true),
        });
      } else {
        menuActions.push({
          key: 'cancel',
          label: 'Cancelar pedido',
          icon: X,
          danger: true,
          onClick: () => handleCancel(menuOrder.id, true),
        });
      }
    }
  }

  const menuHeight = menuActions.length * 40 + 16;
  const menuOpensUp = Boolean(
    actionsMenu && actionsMenu.bottom + 8 + menuHeight > window.innerHeight
  );
  const menuTop = actionsMenu
    ? (menuOpensUp ? Math.max(8, actionsMenu.top - menuHeight - 6) : actionsMenu.bottom + 6)
    : 0;
  const menuLeft = actionsMenu ? Math.max(8, actionsMenu.right - 248) : 0;

  return (
    <div className="online-sales-page">
      {/* Header */}
      <div className="online-sales-header">
        <div>
          <h1 className="online-sales-title">
            Vendas <span className="online-sales-title-accent">Online</span>
          </h1>
          <p className="online-sales-subtitle">
            Gerencie todos os pedidos do e-commerce
          </p>
        </div>
        <button
          type="button"
          className="online-sales-refresh-btn"
          onClick={() => loadOrders()}
          disabled={loading}
          title="Atualizar pedidos"
        >
          <RefreshCw size={16} className={loading ? 'online-sales-refresh-spin' : undefined} />
          Atualizar
        </button>
      </div>

      {/* Filters */}
      <div className="online-sales-filters">
        <form onSubmit={handleSearch} className="online-sales-search-form">
          <div className="online-sales-search">
            <Search className="online-sales-search-icon" size={14} />
            <input
              type="text"
              placeholder="Buscar por número, cliente ou e-mail..."
              className="online-sales-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button type="submit" className="online-sales-search-button">
            Buscar
          </button>
        </form>
      </div>

      {/* Workflow - Filtro por Status */}
      <div className="incoming-dashboard-workflow">
        <div className="incoming-dashboard-workflow-header">
          <h3>
            <Filter size={16} />
            Filtro por Status
          </h3>
          {statusFilter && (
            <button
              className="incoming-dashboard-workflow-clear-filter"
              onClick={() => setStatusFilter(null)}
            >
              <X size={14} />
              Limpar Filtro
            </button>
          )}
        </div>
        <div className="incoming-dashboard-workflow-grid">
          {workflowSteps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = statusFilter === step.label;
            return (
              <div
                key={idx}
                className={`incoming-dashboard-workflow-step ${step.color} ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (isActive) {
                    setStatusFilter(null);
                  } else {
                    setStatusFilter(step.label);
                  }
                }}
              >
                <div className="incoming-dashboard-workflow-icon">
                  <Icon size={24} />
                  {step.count > 0 && (
                    <div className="incoming-dashboard-workflow-badge">
                      {step.count}
                    </div>
                  )}
                </div>
                <p className="incoming-dashboard-workflow-label">{step.label}</p>
                {idx < workflowSteps.length - 1 && (
                  <div className="incoming-dashboard-workflow-connector" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="online-sales-loading">
          <Loader2 size={32} className="online-sales-loading-icon" />
          <p>Carregando pedidos...</p>
        </div>
      ) : (
        <div className="online-sales-card">
          <div className="online-sales-table-wrapper">
            <table className="online-sales-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>E-mail</th>
                  <th>Data</th>
                  <th className="text-right">Total</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="online-sales-empty">
                      <Package size={48} className="online-sales-empty-icon" />
                      <p>Nenhum pedido encontrado</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => {
                    const statusConfig = getStatusConfig(order.status);
                    const StatusIcon = statusConfig.icon;

                    return (
                      <tr key={order.id} className="online-sales-table-row">
                        <td>
                          <div className="online-sales-order-info">
                            <span className="online-sales-order-number">{order.order_number}</span>
                          </div>
                        </td>
                        <td>
                          <span className="online-sales-customer-name">{order.customer_name}</span>
                        </td>
                        <td>
                          <span className="online-sales-customer-email">{order.customer_email}</span>
                        </td>
                        <td>
                          <span className="online-sales-date">{formatDate(order.created_at)}</span>
                        </td>
                        <td className="text-right">
                          <span className="online-sales-total">{formatCurrency(order.total_amount)}</span>
                        </td>
                        <td className="text-center">
                          <div className={`online-sales-status-badge ${statusConfig.color}`}>
                            <StatusIcon size={14} />
                            <span>{statusConfig.label}</span>
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="online-sales-actions">
                            <button
                              type="button"
                              className="online-sales-action-btn"
                              title="Opções"
                              onClick={(event) => {
                                event.stopPropagation();
                                const rect = event.currentTarget.getBoundingClientRect();
                                setActionsMenu((current) => (
                                  current?.orderId === order.id
                                    ? null
                                    : {
                                      orderId: order.id,
                                      top: rect.top,
                                      bottom: rect.bottom,
                                      right: rect.right,
                                    }
                                ));
                              }}
                            >
                              <SlidersVertical size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status Update Confirmation Modal */}
      {showStatusModal && statusModalOrder && nextStatus && (
        <div
          className="incoming-dashboard-modal-overlay"
          onClick={() => !updatingStatus && setShowStatusModal(false)}
        >
          <div
            className="incoming-dashboard-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="incoming-dashboard-modal-content">
              <div className="incoming-dashboard-modal-header">
                <div className="incoming-dashboard-modal-header-left">
                  <div className="incoming-dashboard-modal-icon">
                    <CheckCircle2 size={24} />
                  </div>
                  <div>
                    <h2 className="incoming-dashboard-modal-title">Avançar Pedido</h2>
                    <p className="incoming-dashboard-modal-subtitle">
                      Confirme para avançar o pedido para a próxima etapa do fluxo.
                    </p>
                  </div>
                </div>
                {!updatingStatus && (
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="incoming-dashboard-modal-close"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <div className="incoming-dashboard-modal-body">
                <p style={{ marginBottom: '1rem' }}>
                  Deseja realmente avançar o pedido{' '}
                  <strong>{statusModalOrder.order_number}</strong> de{' '}
                  <strong>{getStatusLabel(statusModalOrder.status)}</strong> para{' '}
                  <strong>{getStatusLabel(nextStatus)}</strong>?
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button
                    className="incoming-dashboard-btn-secondary"
                    onClick={() => setShowStatusModal(false)}
                    disabled={updatingStatus}
                  >
                    Voltar
                  </button>
                  <button
                    className="incoming-dashboard-btn-primary"
                    onClick={handleConfirmStatusUpdate}
                    disabled={updatingStatus}
                  >
                    {updatingStatus ? (
                      <>
                        <Loader2 size={16} className="incoming-dashboard-spinner" />
                        Atualizando...
                      </>
                    ) : (
                      'Confirmar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="online-sales-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="online-sales-modal" onClick={(e) => e.stopPropagation()}>
            <div className="online-sales-modal-header">
              <h2 className="online-sales-modal-title">Detalhes do Pedido</h2>
              <button
                className="online-sales-modal-close"
                onClick={() => setShowDetailModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            {detailLoading ? (
              <div className="online-sales-modal-loading">
                <Loader2 size={32} className="online-sales-loading-icon" />
                <p>Carregando detalhes...</p>
              </div>
            ) : orderDetail ? (
              <div className="online-sales-modal-content">
                {/* Order Info */}
                <div className="online-sales-detail-section">
                  <h3 className="online-sales-detail-section-title">Informações do Pedido</h3>
                  <div className="online-sales-detail-grid">
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">Número:</span>
                      <span className="online-sales-detail-value">{orderDetail.order_number}</span>
                    </div>
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">Status:</span>
                      {(() => {
                        const statusConfig = getStatusConfig(orderDetail.status);
                        const StatusIcon = statusConfig.icon;
                        return (
                          <div className={`online-sales-status-badge ${statusConfig.color}`}>
                            <StatusIcon size={14} />
                            <span>{statusConfig.label}</span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">Data:</span>
                      <span className="online-sales-detail-value">{formatDate(orderDetail.created_at)}</span>
                    </div>
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">Total:</span>
                      <span className="online-sales-detail-value">{formatCurrency(orderDetail.total_amount)}</span>
                    </div>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="online-sales-detail-section">
                  <h3 className="online-sales-detail-section-title">Cliente</h3>
                  <div className="online-sales-detail-grid">
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">Nome:</span>
                      <span className="online-sales-detail-value">{orderDetail.customer_name}</span>
                    </div>
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">E-mail:</span>
                      <span className="online-sales-detail-value">{orderDetail.customer_email}</span>
                    </div>
                    <div className="online-sales-detail-item">
                      <span className="online-sales-detail-label">CPF/CNPJ:</span>
                      <span className="online-sales-detail-value">{orderDetail.customer_cpf_cnpj || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Shipping Info */}
                {orderDetail.shipping_street && (
                  <div className="online-sales-detail-section">
                    <h3 className="online-sales-detail-section-title">Endereço de Entrega</h3>
                    <div className="online-sales-detail-address">
                      <p>{orderDetail.shipping_street}, {orderDetail.shipping_number}</p>
                      {orderDetail.shipping_complement && <p>{orderDetail.shipping_complement}</p>}
                      <p>
                        {orderDetail.shipping_locality} - {orderDetail.shipping_city}/{orderDetail.shipping_region_code}
                      </p>
                      <p>CEP: {orderDetail.shipping_postal_code}</p>
                    </div>
                  </div>
                )}

                {/* Items */}
                {orderDetail.items && orderDetail.items.length > 0 && (
                  <div className="online-sales-detail-section">
                    <h3 className="online-sales-detail-section-title">Itens do Pedido</h3>
                    <div className="online-sales-detail-items">
                      {orderDetail.items.map((item: any) => (
                        <div key={item.id} className="online-sales-detail-item-row">
                          <div className="online-sales-detail-item-info">
                            <span className="online-sales-detail-item-name">
                              {item.product_name || item.product_name_from_db || 'Produto'}
                            </span>
                            {item.product_sku && (
                              <span className="online-sales-detail-item-sku">SKU: {item.product_sku}</span>
                            )}
                          </div>
                          <div className="online-sales-detail-item-quantity">
                            {item.quantity}x
                          </div>
                          <div className="online-sales-detail-item-price">
                            {formatCurrency(item.total_price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(orderDetail.status === 'PresaleIssued' || orderDetail.status === 'NfeIssued') && (
                  <div className="online-sales-detail-section">
                    <h3 className="online-sales-detail-section-title">Documento de saída</h3>
                    <button
                      type="button"
                      className="online-sales-action-btn-act"
                      onClick={() => handlePrintExitDocument(orderDetail)}
                    >
                      <Printer size={14} /> Baixar documento de saída
                    </button>
                  </div>
                )}

                {(orderDetail.has_nfce || orderDetail.has_cupom) && (
                  <div className="online-sales-detail-section">
                    <h3 className="online-sales-detail-section-title">
                      {orderDetail.has_nfce ? 'Cupom fiscal' : 'Cupom'}
                    </h3>
                    <button
                      type="button"
                      className="online-sales-action-btn-act"
                      onClick={() => handleViewReceipt(orderDetail)}
                    >
                      {orderDetail.has_nfce ? <Receipt size={14} /> : <FileText size={14} />}
                      {orderDetail.has_nfce ? ' Ver cupom fiscal (NFC-e)' : ' Ver cupom simples'}
                    </button>
                    {!orderDetail.has_nfce && (
                      <button
                        type="button"
                        className="online-sales-action-btn"
                        style={{ marginLeft: 8 }}
                        title="Download do cupom"
                        onClick={() => handleDownloadReceipt(orderDetail)}
                      >
                        <Download size={14} />
                      </button>
                    )}
                  </div>
                )}

                {orderDetail.status === 'InSeparation' && (
                  <div className="online-sales-detail-section">
                    <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                      Pedido <strong>aguardando na pré-venda</strong>. Avance pela Pré-venda para emitir o documento de saída.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="online-sales-modal-error">
                <p>Erro ao carregar detalhes do pedido</p>
              </div>
            )}
          </div>
        </div>
      )}

      {cupomModalOpen && (
        <div className="online-sales-modal-overlay" onClick={() => setCupomModalOpen(false)}>
          <div className="online-sales-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="online-sales-modal-header">
              <h2 className="online-sales-modal-title">Cupom simples</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  className="online-sales-action-btn"
                  onClick={handleDownloadCupom}
                  disabled={cupomLoading || !cupomTexto}
                  title="Download do cupom"
                >
                  <Download size={18} />
                </button>
                <button className="online-sales-modal-close" onClick={() => setCupomModalOpen(false)}>
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="online-sales-modal-content">
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                Pedido {cupomOrderNumber} · não fiscal
              </p>
              {cupomLoading ? (
                <div className="online-sales-modal-loading">
                  <Loader2 size={32} className="online-sales-loading-icon" />
                  <p>Gerando cupom...</p>
                </div>
              ) : (
                <pre
                  style={{
                    margin: 0,
                    padding: 16,
                    background: '#0f172a',
                    color: '#e2e8f0',
                    borderRadius: 8,
                    fontSize: 12,
                    lineHeight: 1.45,
                    overflow: 'auto',
                    maxHeight: '60vh',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                  }}
                >
                  {cupomTexto}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {actionsMenu && menuOrder && createPortal(
        <>
          <button
            type="button"
            className="online-sales-menu-backdrop"
            aria-label="Fechar opções"
            onClick={() => setActionsMenu(null)}
          />
          <div
            className="online-sales-menu"
            role="menu"
            style={{ top: menuTop, left: menuLeft }}
          >
            {menuActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  type="button"
                  role="menuitem"
                  className={action.danger ? 'is-danger' : undefined}
                  onClick={() => {
                    setActionsMenu(null);
                    action.onClick();
                  }}
                >
                  <Icon size={16} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}

      <NfceViewerModal
        isOpen={nfceModalOpen}
        onClose={() => {
          setNfceModalOpen(false);
          setNfceSaleId(null);
          setNfceSaleNumber(null);
        }}
        saleId={nfceSaleId}
        saleNumber={nfceSaleNumber}
      />
    </div>
  );
}

export default OnlineSalesPage;
